import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import PhoneInTalkRoundedIcon from '@mui/icons-material/PhoneInTalkRounded';
import { DiffPills } from './DiffPills';
import {
  formatLogTime,
  fmtStatusLabel,
  fmtBoolLabel,
  fmtNoteLabel,
  shouldShowTooltip,
} from '../utils/marketingLeadHelpers';

export const TH_SX = {
  fontFamily: 'Inter Bold, sans-serif',
  fontWeight: 700,
  fontSize: 11,
  color: '#4B5563',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  bgcolor: '#F1F2F4',
  borderBottom: '2px solid #D1D5DB',
  borderRight: '1px solid #E5E7EB',
  py: 1.35,
  px: 1.5,
  whiteSpace: 'nowrap',
  textAlign: 'center',
  '&:last-of-type': { borderRight: 'none' },
};

export const TD_SX = {
  fontFamily: 'Inter Regular, sans-serif',
  fontSize: 13,
  color: '#111111',
  borderBottom: '1px solid #E5E7EB',
  borderRight: '1px solid #E5E7EB',
  py: 2,
  px: 1.5,
  verticalAlign: 'middle',
  '&:last-of-type': { borderRight: 'none' },
};

function fmtMeetingAtLabel(val) {
  if (val === null || val === undefined || val === '') return null;
  try {
    return formatLogTime(val);
  } catch (_) {
    return String(val);
  }
}

export function StatusLogsTable({ logs }) {
  if (!Array.isArray(logs) || logs.length === 0) {
    return (
      <Box sx={{ py: 5, textAlign: 'center' }}>
        <HistoryRoundedIcon sx={{ fontSize: 38, color: '#E5E7EB', mb: 1.5, display: 'block', mx: 'auto' }} />
        <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 13, color: '#9CA3AF' }}>
          No status logs yet.
        </Typography>
      </Box>
    );
  }

  const sorted = [...logs]
    .filter((log) => {
      const fields = String(log.property_changed || '')
        .split(',')
        .map((f) => f.trim().toLowerCase())
        .filter(Boolean);
      // skip rows where should_contact is the only changed field
      return !(fields.length === 1 && fields[0] === 'should_contact');
    })
    .sort((a, b) => new Date(b.changed_at) - new Date(a.changed_at));

  return (
    <Box
      sx={{
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1.5px solid #D1D5DB',
        boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
      }}
    >
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size='small' sx={{ minWidth: 960, borderCollapse: 'collapse' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...TH_SX, textAlign: 'left' }}>User</TableCell>
              <TableCell sx={TH_SX}>Status (old / new)</TableCell>
              <TableCell sx={TH_SX}>Meeting (old / new)</TableCell>
              <TableCell sx={TH_SX}>Call Status</TableCell>
              <TableCell sx={TH_SX}>Marketing / Call Notes</TableCell>
              <TableCell sx={TH_SX}>SMS Sent</TableCell>
              <TableCell sx={{ ...TH_SX, textAlign: 'right' }}>Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((log, idx) => {
              const lv = log.last_value || {};
              const nv = log.new_value || {};
              const changedFields = String(log.property_changed || '')
                .split(',')
                .map((f) => f.trim().toLowerCase())
                .filter(Boolean);

              const statusChanged = changedFields.includes('current_status');
              const meetingChanged = changedFields.includes('meeting_scheduled_at');
              const notesChanged = changedFields.includes('marketing_notes');
              const smsChanged = changedFields.includes('sms_sent');
              const hasCallLogPart = changedFields.includes('call_log');

              const oldCallStatus =
                lv.call_status ?? log.previous_call_status ?? null;
              const newCallStatus =
                nv.call_status ?? log.new_call_status ?? log.call_status ?? null;
              const oldCallingNote =
                lv.calling_note ?? log.previous_calling_note ?? null;
              const newCallingNote =
                nv.calling_note ??
                log.new_calling_note ??
                log.calling_note ??
                log.user_note ??
                null;

              const oldMeetingAt = lv.meeting_scheduled_at ?? null;
              const newMeetingAt = nv.meeting_scheduled_at ?? null;

              const showLeadStatusDiff =
                statusChanged ||
                lv.current_status !== undefined ||
                nv.current_status !== undefined;
              const showMeetingDiff =
                meetingChanged ||
                (oldMeetingAt != null && oldMeetingAt !== '') ||
                (newMeetingAt != null && newMeetingAt !== '');
              const showCallStatusDiff =
                hasCallLogPart ||
                (oldCallStatus != null && String(oldCallStatus).trim() !== '') ||
                (newCallStatus != null && String(newCallStatus).trim() !== '');

              const notesCellLabelSx = {
                fontFamily: 'Inter Bold, sans-serif',
                fontWeight: 700,
                fontSize: 10,
                color: '#6B7280',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                mb: 0.35,
              };

              return (
                <TableRow
                  key={log._id || idx}
                  sx={{
                    bgcolor: idx % 2 === 0 ? '#FFFFFF' : '#F9FAFB',
                    '&:last-child td, &:last-child th': { borderBottom: 'none' },
                    transition: 'background 0.12s',
                    '&:hover td': { bgcolor: '#EEF0F3' },
                  }}
                >
                  {/* User */}
                  <TableCell sx={{ ...TD_SX, minWidth: 120, width: 160, whiteSpace: 'nowrap' }}>
                    <Tooltip
                      title={`${log.changed_by_name || '—'}${log.changed_by_role ? ` (${String(log.changed_by_role).replace(/_/g, ' ')})` : ''}`}
                      placement='top'
                      arrow
                      disableHoverListener={!shouldShowTooltip(
                        `${log.changed_by_name || '—'}${log.changed_by_role ? ` (${String(log.changed_by_role).replace(/_/g, ' ')})` : ''}`,
                        30
                      )}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'Inter Bold, sans-serif',
                          fontWeight: 700,
                          fontSize: 13,
                          color: '#111111',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {log.changed_by_name || '—'}
                        {log.changed_by_role ? ` (${String(log.changed_by_role).replace(/_/g, ' ')})` : ''}
                      </Typography>
                    </Tooltip>
                  </TableCell>

                  {/* Lead status old / new */}
                  <TableCell sx={{ ...TD_SX, minWidth: 140, width: 160, textAlign: 'center' }}>
                    {showLeadStatusDiff ? (
                      <DiffPills
                        oldLabel={fmtStatusLabel(lv.current_status ?? log.previous_status)}
                        newLabel={fmtStatusLabel(nv.current_status ?? log.new_status)}
                        emptyText='—'
                        fluid
                      />
                    ) : (
                      <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 12, color: '#D1D5DB', textAlign: 'center' }}>—</Typography>
                    )}
                  </TableCell>

                  {/* Meeting scheduled old / new */}
                  <TableCell sx={{ ...TD_SX, minWidth: 150, width: 180, textAlign: 'center' }}>
                    {showMeetingDiff ? (
                      <DiffPills
                        oldLabel={fmtMeetingAtLabel(oldMeetingAt)}
                        newLabel={fmtMeetingAtLabel(newMeetingAt)}
                        emptyText='—'
                        fluid
                      />
                    ) : (
                      <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 12, color: '#D1D5DB', textAlign: 'center' }}>—</Typography>
                    )}
                  </TableCell>

                  {/* Call status old / new */}
                  <TableCell sx={{ ...TD_SX, minWidth: 140, width: 180, textAlign: 'center' }}>
                    {showCallStatusDiff ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', gap: 0.5 }}>
                        {hasCallLogPart ? (
                          <PhoneInTalkRoundedIcon sx={{ fontSize: 13, color: '#94A3B8' }} />
                        ) : null}
                        <DiffPills
                          oldLabel={fmtStatusLabel(oldCallStatus)}
                          newLabel={fmtStatusLabel(newCallStatus)}
                          emptyText='—'
                          fluid
                        />
                      </Box>
                    ) : (
                      <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 12, color: '#D1D5DB', textAlign: 'center' }}>—</Typography>
                    )}
                  </TableCell>

                  {/* Marketing notes + calling notes (stack when both) */}
                  <TableCell sx={{ ...TD_SX, minWidth: 180, width: 260, textAlign: 'center' }}>
                    {notesChanged || hasCallLogPart ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'center', width: '100%' }}>
                        {notesChanged ? (
                          <Box sx={{ width: '100%' }}>
                            {hasCallLogPart ? (
                              <Typography sx={{ ...notesCellLabelSx, textAlign: 'center' }}>Marketing</Typography>
                            ) : null}
                            <DiffPills
                              oldLabel={fmtNoteLabel(lv.marketing_notes ?? log.previous_marketing_note)}
                              newLabel={fmtNoteLabel(nv.marketing_notes ?? log.new_marketing_note)}
                              emptyText='no note'
                              fluid
                            />
                          </Box>
                        ) : null}
                        {hasCallLogPart ? (
                          <Box sx={{ width: '100%' }}>
                            {notesChanged ? <Typography sx={{ ...notesCellLabelSx, textAlign: 'center' }}>Call</Typography> : null}
                            <DiffPills
                              oldLabel={fmtNoteLabel(oldCallingNote)}
                              newLabel={fmtNoteLabel(newCallingNote)}
                              emptyText='no note'
                              fluid
                            />
                          </Box>
                        ) : null}
                      </Box>
                    ) : (
                      <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 12, color: '#D1D5DB', textAlign: 'center' }}>—</Typography>
                    )}
                  </TableCell>

                  {/* SMS sent */}
                  <TableCell sx={{ ...TD_SX, textAlign: 'center' }}>
                    {smsChanged ? (
                      <DiffPills
                        oldLabel={fmtBoolLabel(lv.sms_sent)}
                        newLabel={fmtBoolLabel(nv.sms_sent)}
                      />
                    ) : (
                      <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 12, color: '#D1D5DB', textAlign: 'center' }}>—</Typography>
                    )}
                  </TableCell>

                  {/* Date */}
                  <TableCell sx={{ ...TD_SX, textAlign: 'right', whiteSpace: 'nowrap', width: 145 }}>
                    <Typography
                      sx={{
                        fontFamily: 'Inter Regular, sans-serif',
                        fontSize: 11.5,
                        color: '#9CA3AF',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {formatLogTime(log.changed_at)}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
